import { useCallback } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router';
import { type SignInInput, signInSchema } from '#shared/types';
import logo from '#client/assets/images/ndc-logo.png';
import { Banner } from '#client/components/ui/Banner';
import { Button } from '#client/components/ui/Button';
import { PasswordField } from '#client/components/ui/PasswordField';
import { TextField } from '#client/components/ui/TextField';
import { ThemeToggle } from '#client/components/ui/ThemeToggle';
import { useFormSubmit } from '#client/hooks/useFormSubmit';
import { useFormValues } from '#client/hooks/useFormValues';
import { routes } from '#client/lib/routes';
import { useAuth } from '#client/providers/AuthProvider';
import { LabArtwork } from './LabArtwork';

interface LocationState {
  from?: { pathname: string } | undefined;
}

export function LoginPage() {
  const { user, initializing, signIn } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const { values, set } = useFormValues<SignInInput>({ email: '', password: '' });
  const from = (location.state as LocationState | null)?.from?.pathname ?? routes.dashboard;

  // Hooks run before the redirect below, which is why the early return sits
  // after them rather than at the top of the component.
  const handleSignedIn = useCallback(
    () => void navigate(from, { replace: true }),
    [navigate, from],
  );

  /*
   * The same submit pipeline every other form uses. `successMessage` is
   * optional because login has nothing to toast about.
   */
  const form = useFormSubmit({
    schema: signInSchema,
    submit: signIn,
    onSuccess: handleSignedIn,
  });

  if (!initializing && user) return <Navigate to={from} replace />;

  return (
    <div className="c-login">
      <div className="c-login__panel">
        <div className="c-login__theme">
          <ThemeToggle />
        </div>

        <form className="c-login__form" onSubmit={form.handleSubmit(values)} noValidate>
          <img className="c-login__logo" src={logo} alt="NDC" />
          <div className="c-login__heading">
            <h1 className="c-login__title">Welcome back</h1>
            <p className="c-login__subtitle">Sign in to NDC diagnostic lab management</p>
          </div>

          {/* Keyed on the message so a repeated failure replays the entrance. */}
          {form.formError && (
            <Banner key={form.formError} tone="critical" className="c-login__error">
              {form.formError}
            </Banner>
          )}

          <TextField
            label="Email"
            type="email"
            name="email"
            autoComplete="username"
            placeholder="name@example.com"
            value={values.email}
            error={form.errors.email}
            onChange={(event) => set('email', event.target.value)}
            required
          />

          <PasswordField
            label="Password"
            name="password"
            autoComplete="current-password"
            value={values.password}
            error={form.errors.password}
            onChange={(event) => set('password', event.target.value)}
            required
          />

          <Button
            type="submit"
            variant="primary"
            size="lg"
            fullWidth
            loading={form.submitting}
            className="c-login__submit"
          >
            Sign in
          </Button>
        </form>

        <p className="c-login__footer">© {new Date().getFullYear()} NDC Diagnostic Centre</p>
      </div>

      <LabArtwork />
    </div>
  );
}
